import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

const RETRY_PREFIX = 'lazy-retry:'

function getRetryStorageKey(retryKey: string): string {
  return `${RETRY_PREFIX}${retryKey}`
}

export function isRecoverableChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '')
  const name = error instanceof Error ? error.name : ''
  const normalized = `${name} ${message}`.toLowerCase()

  return (
    normalized.includes('failed to fetch dynamically imported module') ||
    normalized.includes('importing a module script failed') ||
    normalized.includes('chunkloaderror') ||
    normalized.includes('error loading dynamically imported module')
  )
}

export const lazyWithRetryRuntime = {
  reloadPage(): void {
    window.location.reload()
  },
}

export async function loadModuleWithRetry<T>(
  loader: () => Promise<T>,
  retryKey: string,
): Promise<T> {
  try {
    const module = await loader()
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(getRetryStorageKey(retryKey))
    }
    return module
  } catch (error) {
    if (
      typeof window === 'undefined' ||
      !isRecoverableChunkError(error)
    ) {
      throw error
    }

    const storageKey = getRetryStorageKey(retryKey)
    const hasRetried = window.sessionStorage.getItem(storageKey) === '1'

    if (!hasRetried) {
      window.sessionStorage.setItem(storageKey, '1')
      lazyWithRetryRuntime.reloadPage()
      return new Promise<T>(() => undefined)
    }

    throw error
  }
}

export function lazyWithRetry<T extends ComponentType<any>>(
  loader: () => Promise<{ default: T }>,
  retryKey: string,
): LazyExoticComponent<T> {
  return lazy(() => loadModuleWithRetry(loader, retryKey))
}
